import { useEffect } from 'react';
import styled from '@emotion/styled';
import H2 from '@base/H2/H2';
import H4 from '@base/H4/H4';
import P1 from '@base/P1/P1';
import Button from '@base/Button/Button';
import { Fonts, Spacings, mediaQuery } from '@styles/index';
import { getImage } from '@utils/imageHelper';
import {
  RANK_PREDICTOR_RESULT_SHOWN,
  RANK_PREDICTOR_AGAIN_CLICKED
} from '@constants/segment';
import { segment } from '@actions/analytics';
import useWindowSize from '@hooks/useWindowSize';

const RankCardWrapper = styled.div`
  display: flex;
  flex-direction: column;
  gap: ${Spacings.SPACING_10B};
  ${mediaQuery.uptoMobile} {
    gap: ${Spacings.SPACING_6B};
  }
`;

const StyButton = styled(Button)`
  ${mediaQuery.uptoMobile} {
    width: 100%;
  }
`;

const StyRankCard = styled.div`
  position: relative;
  border: 1px solid var(--color-divider);
  border-radius: ${Spacings.SPACING_2B};
  padding: ${Spacings.SPACING_6B};
`;

const StyledImage = styled.img`
  height: ${Spacings.SPACING_35B};
  width: ${Spacings.SPACING_35B};
  top: ${Spacings.SPACING_0B};
  right: ${Spacings.SPACING_0B};
  position: absolute;
  border-radius: ${Spacings.SPACING_2B};
`;

const SmallScoreText = styled(P1)`
  font-size: ${Fonts.ULTRA_SMALL};
  color: var(--color-text-secondary);
  margin-bottom: ${Spacings.SPACING_3B};
`;

const SmallScoreTextAlt = styled(SmallScoreText)`
  margin-bottom: ${Spacings.SPACING_1B};
`;

const ScoreText = styled(P1)`
  color: var(--color-text-secondary);
  width: 80%;
`;

const StyH4 = styled(H4)`
  margin-bottom: ${Spacings.SPACING_2B};
`;

const ScoreTextBold = styled(ScoreText)`
  font-weight: ${Fonts.SEMIBOLD};
`;

const NoRankInfo = styled(ScoreTextBold)`
  margin-top: ${Spacings.SPACING_3B};
`;

const LargeScoreText = styled(P1)`
  font-size: ${Fonts.LARGE};
  color: var(--color-text-secondary);
`;

const RankInfo = styled.div`
  font-size: ${Fonts.H2};
  font-weight: ${Fonts.BOLD};
  margin-top: ${Spacings.SPACING_3B};
`;

const Divider = styled.hr`
  margin: ${Spacings.SPACING_6B} 0px;
  border-top: 1px solid var(--color-divider);
`;

const PrecentileInfo = styled.div`
  font-size: ${Fonts.H6};
  font-weight: ${Fonts.SEMIBOLD};
`;

const MarksInfo = styled(PrecentileInfo)`
  margin-bottom: ${Spacings.SPACING_3B};
`;

const QuestionCardWrapper = styled.div`
  display: flex;
  justify-content: space-between;
`;

const QuestionImage = styled.img`
  height: ${Spacings.SPACING_12B};
  width: ${Spacings.SPACING_12B};
  bottom: ${Spacings.SPACING_0B};
  right: ${Spacings.SPACING_0B};
  position: absolute;
  border-radius: ${Spacings.SPACING_2B};
`;

const QuestionCard = styled.div`
  position: relative;
  border: 1px solid var(--color-divider);
  border-radius: ${Spacings.SPACING_2B};
  padding: ${Spacings.SPACING_3B};
  width: 48%;
`;

const ScoreDetailsWrapper = styled.div`
  display: grid;
  grid-template-columns: repeat(4, 1fr);
  grid-column-gap: 10px;
  grid-row-gap: 20px;
  ${mediaQuery.uptoMobile} {
    grid-template-columns: repeat(2, 1fr);
  }
`;

const ScoreDetail = styled.div``;

const OutOfMarks = styled(P1)`
  font-size: ${Fonts.ULTRA_SMALL};
  color: var(--color-text-secondary);
  margin-left: ${Spacings.SPACING_1B};
`;

const OutOfMarksInfo = styled.div`
  display: flex;
  align-items: baseline;
`;

const Disclaimer = styled(P1)`
  color: var(--color-text-secondary);
`;

const RankInfoRow = styled.span`
  ${mediaQuery.uptoTablet} {
    margin-bottom: ${Spacings.SPACING_2B};
    display: block;
  }
`;

const RankCard = ({ setStep, rankData, setRankData }) => {
  const {
    response_url = '',
    branch = 'Na',
    one_mark_attempted = 'Na',
    one_mark_negative = 'Na',
    one_mark_positive = 'Na',
    rank_details: {
      normalized_mark = 'Na',
      rank_estimate = 'Na',
      rank_in_set = 'Na',
      rank_normalized = 'Na',
      score_estimate = 'Na'
    } = {},
    total_attempted = 'Na',
    total_marks = 'Na',
    total_positive = 'Na',
    total_positive_percentage = 'Na',
    two_marks_attempted = 'Na',
    two_marks_negative = 'Na',
    two_marks_positive = 'Na'
  } = rankData ?? {};
  const { isMobile } = useWindowSize();
  const ExamName = 'GATE';
  const attemptedPercentage = total_attempted
    ? ((total_attempted / 65) * 100).toFixed(2)
    : 0;

  useEffect(() => {
    segment.track(RANK_PREDICTOR_RESULT_SHOWN, {
      exam_name: ExamName,
      rank: rank_estimate,
      normalised_rank: rank_normalized,
      total_marks,
      rank_predictor_url: response_url,
      rank_predicted_by: 'gateoverflow',
      branch_name: branch
    });
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, []);

  const predictAgain = () => {
    setStep('urlInput');
    setRankData(null);
    window.scrollTo(0, 0);
    segment.track(RANK_PREDICTOR_AGAIN_CLICKED, { exam_type: ExamName });
  };
  return (
    <RankCardWrapper>
      <H2>Your predicted rank</H2>
      {rank_estimate && rank_estimate !== 'Na' ? (
        <StyRankCard>
          <StyledImage
            src={getImage('rankpredictor/rank.svg')}
            height={60}
            width={60}
          />
          <ScoreText>Rank Estimate</ScoreText>
          <RankInfo>{rank_estimate}</RankInfo>
          <ScoreTextBold>{branch}</ScoreTextBold>
          {branch === 'CE' && (
            <>
              <Divider />
              <PrecentileInfo>
                <RankInfoRow>Normalised Rank : {rank_normalized}</RankInfoRow>
                {!isMobile && ' · '}
                <RankInfoRow>Rank in Set : {rank_in_set}</RankInfoRow>
              </PrecentileInfo>
            </>
          )}
        </StyRankCard>
      ) : (
        <StyRankCard>
          <StyledImage
            src={getImage('rankpredictor/rank.svg')}
            height={60}
            width={60}
          />
          <ScoreText>Rank Estimate</ScoreText>
          <NoRankInfo>
            Rank couldn&apos;t be generated, please come back in few hours
          </NoRankInfo>
        </StyRankCard>
      )}

      <div>
        <StyH4>Rank Analysis</StyH4>
        <LargeScoreText>
          Detailed analysis based on your uploaded response sheet
        </LargeScoreText>
      </div>
      <div>
        <MarksInfo>1 Mark Question</MarksInfo>
        <QuestionCardWrapper>
          <QuestionCard>
            <QuestionImage
              src={getImage('rankpredictor/question.svg')}
              height={44}
              width={44}
            />
            <SmallScoreTextAlt>Total attempted</SmallScoreTextAlt>
            <H4>{one_mark_attempted}/30</H4>
          </QuestionCard>
          <QuestionCard>
            <QuestionImage
              src={getImage('rankpredictor/answer.svg')}
              height={44}
              width={44}
            />
            <SmallScoreTextAlt>+ve / -ve</SmallScoreTextAlt>
            <H4>
              {one_mark_positive}/{one_mark_negative}
            </H4>
          </QuestionCard>
        </QuestionCardWrapper>
      </div>
      <div>
        <MarksInfo>2 Mark Question</MarksInfo>
        <QuestionCardWrapper>
          <QuestionCard>
            <QuestionImage
              src={getImage('rankpredictor/question.svg')}
              height={44}
              width={44}
            />
            <SmallScoreTextAlt>Total attempted</SmallScoreTextAlt>
            <H4>{two_marks_attempted}/35</H4>
          </QuestionCard>
          <QuestionCard>
            <QuestionImage
              src={getImage('rankpredictor/answer.svg')}
              height={44}
              width={44}
            />
            <SmallScoreTextAlt>+ve / -ve</SmallScoreTextAlt>
            <H4>
              {two_marks_positive}/{two_marks_negative}
            </H4>
          </QuestionCard>
        </QuestionCardWrapper>
      </div>
      <StyRankCard>
        <ScoreDetailsWrapper>
          <ScoreDetail>
            <SmallScoreText>Total attempted</SmallScoreText>
            <OutOfMarksInfo>
              <H4>{total_attempted}</H4>
              <OutOfMarks>/65</OutOfMarks>
            </OutOfMarksInfo>
          </ScoreDetail>
          <ScoreDetail>
            <SmallScoreText>Total correct</SmallScoreText>
            <OutOfMarksInfo>
              <H4>{total_positive}</H4>
              <OutOfMarks>/{total_attempted}</OutOfMarks>
            </OutOfMarksInfo>
          </ScoreDetail>
          <ScoreDetail>
            <SmallScoreText>Attempted %</SmallScoreText>
            <OutOfMarksInfo>
              <H4>{attemptedPercentage}</H4>
              <OutOfMarks>%</OutOfMarks>
            </OutOfMarksInfo>
          </ScoreDetail>
          <ScoreDetail>
            <SmallScoreText>Correct %</SmallScoreText>
            <OutOfMarksInfo>
              <H4>{total_positive_percentage}</H4>
              <OutOfMarks>%</OutOfMarks>
            </OutOfMarksInfo>
          </ScoreDetail>
        </ScoreDetailsWrapper>
        <Divider />
        <ScoreDetailsWrapper>
          <ScoreDetail>
            <SmallScoreText>Total Marks</SmallScoreText>
            <OutOfMarksInfo>
              <H4>{total_marks}</H4>
            </OutOfMarksInfo>
          </ScoreDetail>
          <ScoreDetail>
            <SmallScoreText>Normalised marks</SmallScoreText>
            <OutOfMarksInfo>
              <H4>{normalized_mark}</H4>
            </OutOfMarksInfo>
          </ScoreDetail>
          {score_estimate && (
            <ScoreDetail>
              <SmallScoreText>Score out of 1000</SmallScoreText>
              <OutOfMarksInfo>
                <H4>{score_estimate}</H4>
              </OutOfMarksInfo>
            </ScoreDetail>
          )}
        </ScoreDetailsWrapper>
      </StyRankCard>
      <Disclaimer>
        Disclaimer: The estimated rank is based on past years results data of
        the aspirants
      </Disclaimer>
      <StyButton label="Predict again" onClick={predictAgain} />
    </RankCardWrapper>
  );
};

export default RankCard;
